/**
 * Login — Códice Botánico edition.
 * Native + codex styling (no MUI). authService.login logic preserved.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import authService from '../../services/authService';
import { AuthShell, Field, PasswordField, AuthColibri } from './AuthShell';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    setGeneralError('');
  };

  const validate = () => {
    const e = {};
    if (!formData.email) e.email = 'El correo es obligatorio';
    else if (!authService.validateEmail(formData.email)) e.email = 'Correo inválido';
    if (!formData.password) e.password = 'La contraseña es obligatoria';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await authService.login(formData.email, formData.password);
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      setGeneralError(error.message || 'No se pudo iniciar sesión. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      kicker="Nutriólogos y pacientes · Scram"
      title="Bienvenido de nuevo"
      subtitle="Inicia sesión para continuar con tus planes, expedientes y seguimiento nutricional."
      asideTitle="Nutrición que acompaña."
      asideText="Expediente clínico, planes de alimentación SMAE y seguimiento de progreso para nutriólogos — y para cada paciente, su plan, su avance y Huitzi como guía con IA."
    >
      {location.state?.message && (
        <div style={s.notice}>{location.state.message}</div>
      )}
      {generalError && <div role="alert" style={s.error}>⚠ {generalError}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <Field
          label="Correo electrónico" name="email" type="email" autoComplete="email"
          value={formData.email} onChange={handleChange} error={errors.email}
          disabled={loading} autoFocus icon={<MailGlyph />}
        />
        <PasswordField
          label="Contraseña" name="password" autoComplete="current-password"
          value={formData.password} onChange={handleChange} error={errors.password}
          disabled={loading}
        />

        <div style={{ textAlign: 'right', marginTop: 4 }}>
          <Link to="/auth/forgot-password" style={s.subtleLink}>¿Olvidaste tu contraseña?</Link>
        </div>

        <button type="submit" disabled={loading} className="cdx-btn cdx-btn--jade" style={s.submit}>
          {loading ? 'Entrando…' : 'Iniciar sesión'}
        </button>

        <div style={s.dividerRow}>
          <span style={s.divLine} /> <span style={s.divText}>o</span> <span style={s.divLine} />
        </div>

        <p style={s.footText}>
          ¿No tienes cuenta?{' '}
          <Link to="/auth/register" style={s.strongLink}>Crear cuenta gratis</Link>
        </p>
      </form>
    </AuthShell>
  );
};

const MailGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" strokeLinecap="round" />
  </svg>
);

const s = {
  notice: { background: 'var(--cdx-jade-wash)', color: 'var(--cdx-jade-deep)', border: '1px solid var(--cdx-jade-bright)', borderRadius: 'var(--cdx-r-md)', padding: '10px 14px', fontSize: 13.5, marginBottom: 14 },
  error: { background: 'var(--cdx-jamaica-wash)', color: 'var(--cdx-jamaica)', border: '1px solid var(--cdx-jamaica-soft)', borderRadius: 'var(--cdx-r-md)', padding: '10px 14px', fontSize: 13.5, marginBottom: 14 },
  // display:inline-block + padding: un enlace de 13px medía 16px de alto, imposible de atinar con el pulgar.
  subtleLink: { display: 'inline-block', padding: '12px 4px', minHeight: 44, boxSizing: 'border-box', fontSize: 13, color: 'var(--cdx-turquesa)', textDecoration: 'none', fontFamily: 'var(--cdx-font-body)' },
  submit: { width: '100%', padding: '14px', fontSize: 16, marginTop: 18 },
  dividerRow: { display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' },
  divLine: { flex: 1, height: 1, background: 'var(--cdx-paper-edge)' },
  divText: { fontFamily: 'var(--cdx-font-mono)', fontSize: 12, color: 'var(--cdx-ink-faint)' },
  footText: { textAlign: 'center', fontSize: 14, color: 'var(--cdx-ink-soft)' },
  strongLink: { display: 'inline-block', padding: '12px 4px', minHeight: 44, boxSizing: 'border-box', color: 'var(--cdx-copal)', fontWeight: 700, textDecoration: 'none' },
};

export { AuthColibri };
export default Login;
